import { Lightning } from '@lightningjs/sdk';
import { getScreenSize } from '../tornado/utils/sizes';
import { getBufferStats } from '../tornado/utils/virtualization';
import { getScheduledCount } from '../tornado/utils/placement';
import MetricRow from './metricRow.component';
import {
  ALERT_COLOR,
  FONT_SIZE,
  getGraphBarWidth,
  getInitialStyle,
  getInnerWidth,
  getHeaderHeight,
  getRowHeight,
  GRAPH_HEIGHT,
  GRAPH_SAMPLES,
  METRIC_COLORS,
  PADDING,
} from './performanceMonitor.style';
import {
  DROPPED_FRAME_MS,
  MEGA,
  REFRESH_INTERVAL_MS,
  SAMPLE_INTERVAL_MS,
  TARGET_FPS,
  TEXTURE_PRESSURE_ALERT,
} from './performanceMonitor.constants';

/**
 * A development readout of the things that actually decide how this app feels on a low-end box.
 *
 * Mounted only with `?perf=true`. Every metric gets the same shape — name, current reading, strip — because a spike
 * is the thing worth seeing and an averaged number is exactly what hides it. The metrics are the ones the layout
 * virtualization work turns on:
 *
 * - **fps** — fixed 0..TARGET_FPS scale so the strip is comparable between runs. The reading carries the slowest
 *   frame and the dropped count alongside, which is what gets perceived as the navigation lagging behind the remote
 * - **tex** — texture memory against the stage `memoryPressure` budget, on a fixed 0..budget scale, with the GC
 *   count alongside since they are the same story. Some platforms run a deliberately tightened budget, and there
 *   this pins at the ceiling and textures are re-decoded constantly, capping what everything else can achieve.
 *   The reading turns red past TEXTURE_PRESSURE_ALERT
 * - **churn** — texture bytes newly uploaded to the GPU, per second. `stage.usedVram` is a running total that
 *   Lightning never decrements (its `_updateVramUsage(…, -1)` runs after the native texture has already been
 *   nulled, so it returns at its own guard), so the total itself only ever grows and says nothing about current
 *   usage. Its increments are accurate though, so the rate of change is: flat means textures are stable, sustained
 *   spikes mean they are being re-uploaded — the same story as `gc`
 * - **draw** — quad operations in the last rendered frame, i.e. how well the batching is holding up
 * - **pool** — views parked in the virtualization buffer, with the placement queue depth alongside
 * - **heap** — only where the engine reports it
 */
export default class PerformanceMonitor extends Lightning.Component {
  static _template() {
    return getInitialStyle();
  }

  _construct() {
    this._screenSize = getScreenSize();
    this._frames = 0;
    this._droppedFrames = 0;
    this._worstFrameMs = 0;
    this._drawCalls = 0;
    this._gcCount = 0;
    this._lastSeenGcFrame = 0;
    this._windowStartedAt = 0;
    this._sampleStartedAt = 0;
    this._sampleFrames = 0;
    this._sampleVram = 0;
    this._windowVram = 0;
    this._rows = {};

    this._onFrameStart = () => this._sampleFrame();
    this._onFrameEnd = () => this._sampleRender();
  }

  _build() {
    const screenSize = this._screenSize;
    const rowHeight = getRowHeight(screenSize);
    const rows = this.tag('Rows');
    const definitions = this._getMetricDefinitions();

    definitions.forEach(({ id, label, max }, index) => {
      this._rows[id] = rows.childList.a({
        type: MetricRow,
        y: index * rowHeight,
        config: {
          label,
          w: getInnerWidth(screenSize),
          fontSize: FONT_SIZE[screenSize],
          headerHeight: getHeaderHeight(screenSize),
          graphHeight: GRAPH_HEIGHT[screenSize],
          samples: GRAPH_SAMPLES,
          barWidth: getGraphBarWidth(screenSize),
          max,
          color: METRIC_COLORS[id],
        },
      });
    });

    this.h = PADDING[screenSize] * 2 + definitions.length * rowHeight;
  }

  _attach() {
    this._windowStartedAt = Date.now();
    this._sampleStartedAt = this._windowStartedAt;
    this._sampleVram = this.stage.usedVram;
    this._windowVram = this._sampleVram;
    this.stage.on('frameStart', this._onFrameStart);
    this.stage.on('frameEnd', this._onFrameEnd);
  }

  _detach() {
    this.stage.off('frameStart', this._onFrameStart);
    this.stage.off('frameEnd', this._onFrameEnd);
  }

  /**
   * `max: 0` leaves a strip to scale to its visible window
   *
   * @returns {Array} the rows, in the order they are stacked
   */
  _getMetricDefinitions() {
    const definitions = [
      { id: 'fps', label: 'fps', max: TARGET_FPS },
      { id: 'tex', label: 'tex', max: this._getTextureBudget() },
      { id: 'churn', label: 'churn', max: 0 },
      { id: 'draw', label: 'draw', max: 0 },
      { id: 'pool', label: 'pool', max: 0 },
    ];
    const heapLimit = this._getHeapLimit();

    if (heapLimit) {
      definitions.push({ id: 'heap', label: 'heap', max: heapLimit });
    }

    return definitions;
  }

  _getTextureBudget() {
    return this.stage.getOption('memoryPressure') || 1;
  }

  _getHeapLimit() {
    const { memory } = window.performance || {};

    return memory && memory.jsHeapSizeLimit ? memory.jsHeapSizeLimit : 0;
  }

  _sampleFrame() {
    const frameMs = this.stage.dt * 1000;
    const now = Date.now();

    this._frames += 1;
    this._sampleFrames += 1;

    if (frameMs > this._worstFrameMs) {
      this._worstFrameMs = frameMs;
    }

    if (frameMs > DROPPED_FRAME_MS) {
      this._droppedFrames += 1;
    }

    // NOTE: reading the private marker rather than wrapping stage.gc, so the monitor stays a pure observer
    if (this.stage._lastGcFrame !== this._lastSeenGcFrame) {
      this._lastSeenGcFrame = this.stage._lastGcFrame;
      this._gcCount += 1;
    }

    // The strips are sampled more often than the readings are rewritten: pushing a bar is a property write per
    // bar, while a reading is a text texture that only rasterizes again when it actually changes
    if (now - this._sampleStartedAt >= SAMPLE_INTERVAL_MS) {
      this._pushSamples(now);
    }

    if (now - this._windowStartedAt >= REFRESH_INTERVAL_MS) {
      this._refresh(now);
    }
  }

  _sampleRender() {
    const renderState = this.stage.ctx && this.stage.ctx.renderState;

    // Cleared on every render and completed by finish(), so at frameEnd this holds the frame just drawn
    if (renderState && renderState.quadOperations) {
      this._drawCalls = renderState.quadOperations.length;
    }
  }

  _pushSamples(now) {
    const elapsedMs = Math.max(now - this._sampleStartedAt, 1);

    const vram = this.stage.usedVram;

    this._rows.fps.push((this._sampleFrames * 1000) / elapsedMs);
    this._rows.tex.push(this.stage.usedMemory);
    this._rows.churn.push(Math.max(vram - this._sampleVram, 0));
    this._rows.draw.push(this._drawCalls);
    this._rows.pool.push(getBufferStats().total);

    if (this._rows.heap) {
      this._rows.heap.push(window.performance.memory.usedJSHeapSize);
    }

    this._sampleFrames = 0;
    this._sampleVram = vram;
    this._sampleStartedAt = now;
  }

  _refresh(now) {
    const elapsedMs = Math.max(now - this._windowStartedAt, 1);
    const fps = Math.round((this._frames * 1000) / elapsedMs);
    const budget = this._getTextureBudget();
    const texture = this.stage.usedMemory;
    const texturePercent = Math.round((texture / budget) * 100);
    const vram = this.stage.usedVram;

    this._rows.fps.value = `${fps}  ${this._worstFrameMs.toFixed(0)}ms  ${this._droppedFrames} drop`;
    this._rows.tex.value = `${texturePercent}%  ${this._toMegaPixels(texture)}/${this._toMegaPixels(budget)}MP  gc ${
      this._gcCount
    }`;
    this._rows.tex.valueColor = texturePercent >= TEXTURE_PRESSURE_ALERT ? ALERT_COLOR : METRIC_COLORS.tex;
    // NOTE: MB, not MP — usedVram counts w * h * bytesPerPixel
    this._rows.churn.value = `${this._toMegaBytesPerSecond(vram - this._windowVram, elapsedMs)}MB/s`;
    this._rows.draw.value = `${this._drawCalls}`;
    this._rows.pool.value = `${getBufferStats().total}  queue ${getScheduledCount()}`;

    if (this._rows.heap) {
      const { memory } = window.performance;

      this._rows.heap.value = `${Math.round(memory.usedJSHeapSize / MEGA)}/${Math.round(
        memory.jsHeapSizeLimit / MEGA,
      )}MB`;
    }

    this._frames = 0;
    this._droppedFrames = 0;
    this._worstFrameMs = 0;
    this._windowVram = vram;
    this._windowStartedAt = now;
  }

  _toMegaPixels(value) {
    return (value / MEGA).toFixed(1);
  }

  _toMegaBytesPerSecond(bytes, elapsedMs) {
    return (Math.max(bytes, 0) / MEGA / (elapsedMs / 1000)).toFixed(1);
  }
}
